import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowDown } from "lucide-react";
import { scrollToSection } from "./Header";
import { BRAND, HERO_STATS } from "@/data/content";

const EASE = [0.22, 1, 0.36, 1];
const HERO_IMG = "/hero.jpg";

const Line = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section
      id="home"
      ref={ref}
      data-testid="hero-section"
      className="relative flex min-h-screen flex-col justify-center overflow-hidden pt-[72px]"
      style={{
        paddingLeft: "env(safe-area-inset-left)",
        paddingRight: "env(safe-area-inset-right)",
        paddingBottom: "env(safe-area-inset-bottom)",
      }}
    >
      <div
        className="pointer-events-none absolute inset-0 overflow-hidden"
        aria-hidden="true"
      >
        <div className="mesh-blob mesh-blob-a" />
        <div className="mesh-blob mesh-blob-b" />
        <div className="mesh-blob mesh-blob-c" />
        <div className="hero-noise" />
      </div>
      <div className="relative mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-16 px-4 py-20 sm:px-8 lg:grid-cols-[1.15fr_0.85fr] lg:px-16">
        <motion.div style={{ opacity: fade }}>
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="mb-8 font-mono2 text-[11px] uppercase tracking-[0.35em] text-[#F2824F]"
            data-testid="hero-eyebrow"
          >
            {BRAND.name}
          </motion.p>

          <h1
            data-testid="hero-headline"
            className="font-display text-hero font-light tracking-tight text-slate-100"
          >
            <Line delay={0.35}>Capital,</Line>
            <Line delay={0.5}>
              <em className="italic text-[#F26122]">structured</em> around
            </Line>
            <Line delay={0.65}>your business.</Line>
          </h1>

          <motion.p
            data-testid="hero-subheadline"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.0 }}
            className="mt-8 max-w-xl text-base font-light leading-relaxed text-slate-400 sm:text-lg"
          >
            We help businesses and promoters identify the right financing
            structure and connect with relevant capital providers across debt,
            private credit and structured finance.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.15 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-cta-discuss"
              onClick={() => scrollToSection("contact")}
              className="group flex items-center gap-2 bg-[#F26122] px-7 py-4 text-sm font-medium tracking-wide text-[#0B0F17] transition-all duration-300 hover:-translate-y-1 hover:bg-[#FF7B3D]"
            >
              Discuss Your Requirement
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
            <button
              data-testid="hero-cta-capabilities"
              onClick={() => scrollToSection("capabilities")}
              className="border border-white/15 px-7 py-4 text-sm font-light tracking-wide text-slate-300 transition-all duration-300 hover:-translate-y-1 hover:border-[#F26122]/50 hover:text-[#F26122]"
            >
              Explore Our Capabilities
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.4 }}
            className="mt-14 grid max-w-xl grid-cols-3 gap-5"
            data-testid="hero-stats"
          >
            {HERO_STATS.map((s) => (
              <div
                key={s.label}
                data-testid={`hero-stat-${s.label
                  .toLowerCase()
                  .replace(/[^a-z0-9]+/g, "-")}`}
                className="border-l border-[#F26122]/50 pl-4"
              >
                <p className="font-display text-2xl font-light text-slate-100 sm:text-3xl">
                  {s.value}
                </p>
                <p className="mt-1 font-mono2 text-[9px] uppercase leading-relaxed tracking-[0.25em] text-slate-500">
                  {s.label}
                </p>
              </div>
            ))}
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.4, delay: 0.6, ease: EASE }}
          className="relative hidden lg:block"
        >
          <div
            className="absolute -inset-4 border border-[#F26122]/20"
            aria-hidden="true"
          />
          <div className="relative overflow-hidden border border-white/10 shadow-[0_40px_80px_-40px_rgba(0,0,0,0.7)]">
            <motion.img
              src={HERO_IMG}
              alt="Advisor reviewing financial documents beside a laptop"
              style={{ y: imgY }}
              className="h-[560px] w-full scale-110 object-cover grayscale"
              data-testid="hero-image"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F17] via-transparent to-transparent" />
            <div
              className="absolute inset-0"
              style={{
                background:
                  "radial-gradient(ellipse 70% 50% at 50% 20%, rgba(242,97,34,0.08), transparent 60%)",
              }}
            />
          </div>
          <div className="absolute -bottom-5 -left-5 h-24 w-24 border-b border-l border-[#F26122]/40" aria-hidden="true" />
          <div className="absolute -right-5 -top-5 h-24 w-24 border-r border-t border-[#F26122]/40" aria-hidden="true" />
        </motion.div>
      </div>

      <motion.button
        data-testid="hero-scroll-indicator"
        onClick={() => scrollToSection("about")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-8 left-1/2 hidden -translate-x-1/2 flex-col items-center gap-2 text-slate-500 transition-colors hover:text-[#F26122] lg:flex"
        aria-label="Scroll to about section"
      >
        <span className="font-mono2 text-[10px] uppercase tracking-[0.3em]">
          Scroll
        </span>
        <motion.span
          animate={{ y: [0, 6, 0] }}
          transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
        >
          <ArrowDown className="h-4 w-4" />
        </motion.span>
      </motion.button>
    </section>
  );
};

export default Hero;
